import FuseUtils from '@fuse/utils';
import AppContext from 'app/AppContext';
import authRoles, { isRoleEnum, SpecialRolesEnum, UserRolesType } from 'app/auth/authRoles';
import { userPermissionsSelector, userRoleSelector } from 'app/auth/store/user.selector';
import { isFeatureAllowed } from 'app/auth/utils/utils';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { selectUserFranchise } from 'app/main/apps/franchises/ducks/franchises.selector';
import { RootState } from 'app/store';
import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { matchRoutes } from 'react-router-config';
import { withRouter } from 'react-router-dom';

interface FuseAuthorizationState {
  accessGranted: boolean;
  routes: any;
}

interface FuseAuthorizationProps extends ConnectedFuseAuthorizationProps {
  userRole: UserRolesType;
  location: any;
  history: any;
  children: React.ReactNode;
}

class FuseAuthorization extends Component<FuseAuthorizationProps, FuseAuthorizationState> {
  constructor(props: FuseAuthorizationProps, context: any) {
    super(props);
    const { routes } = context;
    this.state = {
      accessGranted: true,
      routes,
    };
  }

  componentDidMount() {
    if (!this.state.accessGranted) {
      this.redirectRoute();
    }
  }

  shouldComponentUpdate(nextProps: any, nextState: FuseAuthorizationState) {
    return nextState.accessGranted !== this.state.accessGranted;
  }

  componentDidUpdate() {
    if (!this.state.accessGranted) {
      this.redirectRoute();
    }
  }

  static getDerivedStateFromProps(props: FuseAuthorizationProps, state: FuseAuthorizationState) {
    const { location, userRole } = props;
    const { pathname } = location;
    const matched = matchRoutes(state.routes, pathname)[0];

    if (!matched) {
      return {
        accessGranted: true,
      };
    }

    const hasPermission = FuseUtils.hasPermission(matched.route.auth, userRole);
    const canAccessToFeature = matched.route.featureRestriction
      ? isFeatureAllowed(matched.route.featureRestriction, props.permissions)
      : true;

    return {
      accessGranted: hasPermission && canAccessToFeature,
    };
  }

  redirectRoute() {
    const { location, userRole, history } = this.props;
    const { pathname, state } = location;
    const redirectUrl = state && state.redirectUrl ? state.redirectUrl : '/';
    /*
    User is guest
    Redirect to Login Page
    */
    if (!userRole || userRole.length === 0) {
      history.push({
        pathname: routesUrl.login,
        state: { redirectUrl: pathname },
      });
      return;
    }

    if (userRole.includes(SpecialRolesEnum.REGISTERED)) {
      /**
       * User has an account but has not paid
       */

      history.push({
        pathname: routesUrl.accountConfirmation,
      });
    } else if (userRole.includes(SpecialRolesEnum.RESTRICTED_STORE_OWNER)) {
      /**
       * User has an account but has no stores
       * Redirect to no stores page
       *  */
      history.push({
        pathname: routesUrl.noStores,
      });
    } else if (isRoleEnum(userRole) && authRoles.admin.includes(userRole)) {
      history.push({
        pathname: routesUrl.administration,
      });
    } else {
      /*
        User is member
        User must be on unAuthorized page or just logged in
        Redirect to dashboard or redirectUrl)
      */
      /*  history.push({
        pathname: routesUrl.forbidden,
      }); */
      history.push({
        pathname: redirectUrl,
      });
    }
  }

  render() {
    return this.state.accessGranted ? this.props.children : null;
  }
}

const mapStateToProps = (state: RootState) => ({
  userRole: userRoleSelector(state),
  franchise: selectUserFranchise(state),
  permissions: userPermissionsSelector(state),
});

const connector = connect(mapStateToProps);

type ConnectedFuseAuthorizationProps = ConnectedProps<typeof connector>;

FuseAuthorization.contextType = AppContext;

export default withRouter(connector(FuseAuthorization as any));
